import { skipToken } from '@reduxjs/toolkit/query';
import { Fragment, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  useGetCourseMarkingSchemeQuery,
  useGetCourseReportQuery,
  useGetCourseUserProfileQuery,
  useGetRequestLeaveByStudentQuery,
  useGetStudentAssignmentReportQuery,
  useGetStudentAttendancePercentageSummaryQuery,
  useGetStudentCourseEvaluationReportQuery,
  useGetYearlyCourseAttendanceReportQuery,
  usePatchCourseAcquiredMarksMutation,
} from '@/state/course';
import type { Course } from '@/types/api/course.types';
import { getInitials } from '@/lib';
import { ChartContainer } from '@/components/ui/chart';
import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp, Eye, Info, Pencil, X } from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  PolarAngleAxis,
  PolarGrid,
  Radar,
  RadarChart,
  XAxis,
  YAxis,
} from 'recharts';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

type Props = {
  tenant: string;
  courseId: number;
  studentId: string;
  courseGradingStructure?: Course['grading_structure'];
  canEdit?: boolean;
  className?: string;
};

type AcquiredMarkInfo = {
  id: number;
  marks: number;
  fullMarks: number;
};

const monthShort = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const getStatusColorClass = (status: number | null, leaveStatus: number | null) => {
  if (leaveStatus) return 'bg-amber-300 dark:bg-amber-700';
  if (status === 1) return 'bg-emerald-500 dark:bg-emerald-400';
  if (status === 2) return 'bg-red-500 dark:bg-red-400';
  return 'bg-muted';
};

const formatPercent = (value: number) => `${Number(value || 0).toFixed(2)}%`;

const ScoreGauge = ({ scorePercentage }: { scorePercentage: number }) => {
  const clamped = Math.max(0, Math.min(100, scorePercentage));
  const radius = 108;
  const circumference = Math.PI * radius;
  const dash = (clamped / 100) * circumference;

  return (
    <div className="relative mx-auto h-[170px] w-[260px]">
      <svg viewBox="0 0 260 170" className="h-full w-full" aria-hidden>
        <path
          d="M 22 148 A 108 108 0 0 1 238 148"
          fill="none"
          stroke="hsl(var(--muted))"
          strokeWidth="14"
          strokeLinecap="round"
        />
        <path
          d="M 22 148 A 108 108 0 0 1 238 148"
          fill="none"
          stroke="hsl(214 88% 68%)"
          strokeWidth="14"
          strokeLinecap="round"
          strokeDasharray={`${dash} ${circumference}`}
        />
      </svg>
    </div>
  );
};

const CourseStudentAnalyticsPanel = ({
  tenant,
  courseId,
  studentId,
  courseGradingStructure = null,
  canEdit = false,
  className,
}: Props) => {
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [showDetailedReport, setShowDetailedReport] = useState(false);
  const [expandedAssessmentRows, setExpandedAssessmentRows] = useState<Record<number, boolean>>({});
  const [editingAcquiredMarkId, setEditingAcquiredMarkId] = useState<number | null>(null);
  const [marksDraft, setMarksDraft] = useState('');

  const { data: profile } = useGetCourseUserProfileQuery(
    tenant && studentId ? { tenant, userId: studentId } : skipToken
  );
  const { data: evaluationDataRaw } = useGetStudentCourseEvaluationReportQuery(
    tenant && studentId ? { tenant, courseId, studentId } : skipToken
  );
  const { data: assignmentReportRaw } = useGetStudentAssignmentReportQuery(
    tenant && studentId ? { tenant, courseId, studentId } : skipToken
  );
  const { data: attendanceSummary } = useGetStudentAttendancePercentageSummaryQuery(
    tenant && studentId ? { tenant, courseId, studentId } : skipToken
  );
  const { data: yearlyAttendanceRaw } = useGetYearlyCourseAttendanceReportQuery(
    tenant && studentId ? { tenant, courseId, studentId, year: selectedYear } : skipToken
  );
  const { data: leaveRequestsRaw } = useGetRequestLeaveByStudentQuery(
    tenant && studentId ? { tenant, courseId, studentId } : skipToken
  );
  const { data: courseReportDataRaw } = useGetCourseReportQuery(
    tenant && studentId ? { tenant, courseId, studentId } : skipToken
  );
  const { data: courseMarkingSchemeRaw } = useGetCourseMarkingSchemeQuery(
    tenant ? { tenant, courseId } : skipToken
  );
  const [patchCourseAcquiredMarks, { isLoading: isPatchingMark }] =
    usePatchCourseAcquiredMarksMutation();

  const evaluationData = evaluationDataRaw ?? [];
  const assignmentReport = assignmentReportRaw?.[0];
  const yearlyAttendance = yearlyAttendanceRaw ?? [];
  const leaveRequests = leaveRequestsRaw ?? [];
  const courseReportData = courseReportDataRaw ?? [];
  const courseMarkingScheme = courseMarkingSchemeRaw ?? [];

  const reportByMarkingSchemeId = useMemo(() => {
    const map = new Map<
      number,
      {
        secured: number;
        acquired?: AcquiredMarkInfo;
      }
    >();

    courseReportData.forEach((reportItem) => {
      const studentRow = reportItem.student.find((row) => row.student?.id === studentId);
      if (!studentRow) return;

      const secured = Number.isFinite(Number(studentRow.acquired_percentage))
        ? Number(studentRow.acquired_percentage)
        : Number(studentRow.full_marks) > 0
          ? (Number(studentRow.marks) / Number(studentRow.full_marks)) * Number(reportItem.course_percentage)
          : 0;

      map.set(reportItem.id, {
        secured,
        acquired: {
          id: studentRow.id,
          marks: Number(studentRow.marks || 0),
          fullMarks: Number(studentRow.full_marks || 0),
        },
      });
    });

    return map;
  }, [courseReportData, studentId]);

  const attendanceWeight = useMemo(
    () =>
      Number(
        courseMarkingScheme.find(
          (item) =>
            item.course_structure.assessment_type === 'Attendance' &&
            item.course_sub_structure === null
        )?.course_percentage ?? 0
      ),
    [courseMarkingScheme]
  );

  const attendanceContribution =
    (Number(attendanceSummary?.attendance_ratio || 0) / 100) * attendanceWeight;

  const radarData = useMemo(
    () =>
      evaluationData.map((item) => ({
        skill: item.skill.name,
        gpa: Number(item.total_evaluated_points || 0),
      })),
    [evaluationData]
  );

  const overallSkillsGpa = useMemo(() => {
    if (!evaluationData.length) return 0;
    const total = evaluationData.reduce(
      (acc, item) => acc + Number(item.total_evaluated_points || 0),
      0
    );
    return total / evaluationData.length;
  }, [evaluationData]);

  const assignmentsBarData = useMemo(() => {
    const total = Number(assignmentReport?.total_assignments || 0);
    const reviewed = Number(assignmentReport?.graded_assignments || 0);
    const notReviewed = Math.max(total - reviewed, 0);
    return [
      { key: 'Total submissions', value: Number(assignmentReport?.submitted_assignments || 0) },
      { key: 'Reviewed', value: reviewed },
      { key: 'Not reviewed', value: notReviewed },
    ];
  }, [assignmentReport]);

  const maxAssignmentBarValue = useMemo(
    () => Math.max(1, ...assignmentsBarData.map((item) => item.value)),
    [assignmentsBarData]
  );

  const scorePercentage = useMemo(() => {
    let score = 0;
    const hasAttendanceInReport = Boolean(
      courseReportData.find((item) => {
        const mapped = courseMarkingScheme.find((scheme) => scheme.id === item.id);
        return (
          mapped?.course_structure.assessment_type === 'Attendance' &&
          mapped?.course_sub_structure === null
        );
      })
    );

    courseReportData.forEach((reportItem) => {
      const data = reportByMarkingSchemeId.get(reportItem.id);
      if (!data) return;
      score += Number(data.secured || 0);
    });

    if (!hasAttendanceInReport) {
      score += attendanceContribution;
    }

    return Number(score.toFixed(2));
  }, [attendanceContribution, courseMarkingScheme, courseReportData, reportByMarkingSchemeId]);

  const grade = useMemo(() => {
    if (!courseGradingStructure?.grades?.length) return 'N/A';
    const matched = courseGradingStructure.grades.find((item) => {
      const min = Math.min(item.lower_threshold, item.upper_threshold);
      const max = Math.max(item.lower_threshold, item.upper_threshold);
      return scorePercentage >= min && scorePercentage <= max;
    });
    return matched?.grade || 'N/A';
  }, [courseGradingStructure?.grades, scorePercentage]);

  const attendanceByMonth = useMemo(() => {
    const dayMap = new Map(yearlyAttendance.map((item) => [item.day, item] as const));
    const start = new Date(Date.UTC(selectedYear, 0, 1));
    const end = new Date(Date.UTC(selectedYear, 11, 31));
    const firstSunday = new Date(start);
    firstSunday.setUTCDate(firstSunday.getUTCDate() - firstSunday.getUTCDay());
    const lastSaturday = new Date(end);
    lastSaturday.setUTCDate(lastSaturday.getUTCDate() + (6 - lastSaturday.getUTCDay()));

    const weeks: Array<Array<{ date: Date; iso: string; inYear: boolean; status: number | null; leave: number | null }>> = [];
    const monthLabelByWeekIndex = new Map<number, string>();
    const monthFirstWeekIndexByMonth = new Map<number, number>();
    const cursor = new Date(firstSunday);
    let weekIndex = 0;

    while (cursor <= lastSaturday) {
      const week: Array<{ date: Date; iso: string; inYear: boolean; status: number | null; leave: number | null }> = [];
      for (let i = 0; i < 7; i += 1) {
        const date = new Date(cursor);
        const iso = `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`;
        const found = dayMap.get(iso);
        const inYear = date.getUTCFullYear() === selectedYear;
        if (inYear && !monthFirstWeekIndexByMonth.has(date.getUTCMonth())) {
          monthFirstWeekIndexByMonth.set(date.getUTCMonth(), weekIndex);
        }
        week.push({
          date,
          iso,
          inYear,
          status: found?.attendance_status ?? null,
          leave: found?.leave_request_status ?? null,
        });
        cursor.setUTCDate(cursor.getUTCDate() + 1);
      }
      weeks.push(week);
      weekIndex += 1;
    }

    monthFirstWeekIndexByMonth.forEach((firstWeekIndex, monthIndex) => {
      monthLabelByWeekIndex.set(firstWeekIndex, monthShort[monthIndex]);
    });

    return { weeks, monthLabelByWeekIndex };
  }, [selectedYear, yearlyAttendance]);

  const rows = useMemo(() => {
    const topLevel = courseMarkingScheme.filter((item) => item.course_sub_structure === null);
    const subRows = courseMarkingScheme.filter((item) => item.course_sub_structure !== null);

    return topLevel.map((top) => {
      const assessmentType = top.course_structure.assessment_type;
      const children = subRows.filter(
        (sub) => sub.course_structure.id === top.course_structure.id
      );

      const childItems = children.map((child) => {
        const report = reportByMarkingSchemeId.get(child.id);
        return {
          id: child.id,
          name: child.marking_structure_name,
          weight: Number(child.course_percentage || 0),
          secured: Number(report?.secured || 0),
          acquired: report?.acquired,
        };
      });

      let secured = Number(reportByMarkingSchemeId.get(top.id)?.secured || 0);
      if (assessmentType === 'Attendance' && !reportByMarkingSchemeId.get(top.id)) {
        secured = attendanceContribution;
      }
      else {
        secured = Number((secured + childItems.reduce((acc, item) => acc + item.secured, 0)).toFixed(2));
      }

      return {
        id: top.id,
        name: top.marking_structure_name,
        assessmentType,
        weight: Number(top.course_percentage || 0),
        secured,
        children: childItems,
      };
    });
  }, [attendanceContribution, courseMarkingScheme, reportByMarkingSchemeId]);

  const isRowCollapsible = (assessmentType: string) => assessmentType !== 'Attendance';
  const isRowExpanded = (rowId: number) => expandedAssessmentRows[rowId] ?? false;
  const toggleRowExpanded = (rowId: number) => {
    setExpandedAssessmentRows((prev) => ({
      ...prev,
      [rowId]: !(prev[rowId] ?? false),
    }));
  };

  const gradingTooltipContent = (
    <div className="space-y-1 text-xs">
      <p className="font-semibold">Grading scheme</p>
      {(courseGradingStructure?.grades || []).map((item) => (
        <p key={item.id}>
          {item.grade}: {item.upper_threshold} - {item.lower_threshold}
        </p>
      ))}
      {!courseGradingStructure?.grades?.length ? <p>N/A</p> : null}
    </div>
  );

  const profileName = `${profile?.profile?.first_name || ''} ${profile?.profile?.last_name || ''}`.trim() || 'Learner';

  const handleUpdateMark = async (acquiredMarkId: number) => {
    const nextMarks = Number(marksDraft);
    if (!Number.isFinite(nextMarks)) {
      toast.error('Please enter a valid marks value.');
      return;
    }
    try {
      await patchCourseAcquiredMarks({
        tenant,
        acquiredMarkId,
        data: { marks: nextMarks },
      }).unwrap();
      toast.success('Marks updated successfully.');
      setEditingAcquiredMarkId(null);
      setMarksDraft('');
    } catch {
      toast.error('Failed to update marks.');
    }
  };

  const rightSummaryCard = (
    <div className="rounded-xl border bg-card p-5">
      <div className="flex flex-col items-center text-center">
        <Avatar className="h-24 w-24 border">
          <AvatarImage src={profile?.profile?.photo_url || undefined} alt={profileName} />
          <AvatarFallback>
            {getInitials(profile?.profile?.first_name || '', profile?.profile?.last_name || '')}
          </AvatarFallback>
        </Avatar>
        <p className="mt-3 text-2xl font-semibold">{profileName}</p>
        <p className="text-sm text-muted-foreground">{profile?.email || '-'}</p>

        <div className="mt-5 w-full">
          <ScoreGauge scorePercentage={scorePercentage} />
          <div className="-mt-8 flex flex-col items-center gap-1">
            <div className="inline-flex items-center gap-1.5 text-5xl font-semibold leading-none text-foreground">
              {grade}
              <Tooltip>
                <TooltipTrigger asChild>
                  <button type="button" className="text-muted-foreground hover:text-foreground">
                    <Info className="h-4 w-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>{gradingTooltipContent}</TooltipContent>
              </Tooltip>
            </div>
            <div className="inline-flex items-center gap-1.5 text-lg text-muted-foreground">
              Score: {Number(scorePercentage).toFixed(1)}%
              <Tooltip>
                <TooltipTrigger asChild>
                  <button type="button" className="text-muted-foreground hover:text-foreground">
                    <Info className="h-4 w-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>{gradingTooltipContent}</TooltipContent>
              </Tooltip>
            </div>
          </div>
        </div>

        {!showDetailedReport ? (
          <button
            type="button"
            className="mt-4 inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
            onClick={() => setShowDetailedReport(true)}
          >
            <Eye className="h-4 w-4" />
            View details
          </button>
        ) : null}

        <p className="mt-3 text-sm text-muted-foreground">Overall skills GPA: {overallSkillsGpa.toFixed(2)}</p>
      </div>
    </div>
  );

  return (
    <TooltipProvider>
      <div className={className ?? 'space-y-4'}>
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_300px]">
          {showDetailedReport ? (
            <div className="rounded-xl border bg-card">
              <div className="flex items-center justify-between border-b p-4">
                <p className="font-medium">User report</p>
                <button
                  type="button"
                  className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-muted"
                  onClick={() => setShowDetailedReport(false)}
                  aria-label="Close details"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[860px] text-sm">
                  <thead className="bg-muted/50 text-left">
                    <tr>
                      <th className="w-[44px] px-4 py-3" />
                      <th className="px-4 py-3 font-medium">Modules</th>
                      <th className="px-4 py-3 font-medium">Assessment weight</th>
                      <th className="px-4 py-3 font-medium">Secured score</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <Fragment key={row.id}>
                        <tr className="border-t">
                          <td className="px-4 py-3">
                            {isRowCollapsible(row.assessmentType) ? (
                              <button
                                type="button"
                                className="inline-flex h-6 w-6 items-center justify-center rounded hover:bg-muted"
                                onClick={() => toggleRowExpanded(row.id)}
                              >
                                {isRowExpanded(row.id) ? (
                                  <ChevronUp className="h-4 w-4" />
                                ) : (
                                  <ChevronDown className="h-4 w-4" />
                                )}
                              </button>
                            ) : null}
                          </td>
                          <td className="px-4 py-3 font-medium">{row.name}</td>
                          <td className="px-4 py-3">{row.weight}%</td>
                          <td className="px-4 py-3">{formatPercent(row.secured)}</td>
                        </tr>
                        {isRowCollapsible(row.assessmentType) && isRowExpanded(row.id)
                          ? row.children.map((child) => (
                              <tr key={child.id} className="border-t bg-muted/20">
                                <td className="px-4 py-3" />
                                <td className="px-4 py-3">{child.name}</td>
                                <td className="px-4 py-3">{child.weight}%</td>
                                <td className="px-4 py-3">
                                  <div className="flex items-start justify-between gap-3">
                                    <div>
                                      <p>{formatPercent(child.secured)}</p>
                                      {child.acquired ? (
                                        <div className="mt-1.5 space-y-1">
                                          <p className="text-xs text-muted-foreground">
                                            Scored {child.acquired.marks} out of {child.acquired.fullMarks}
                                          </p>
                                          <div className="h-1.5 w-28 rounded-full bg-muted">
                                            <div
                                              className="h-1.5 rounded-full bg-primary"
                                              style={{
                                                width: `${Math.min(
                                                  100,
                                                  child.acquired.fullMarks > 0
                                                    ? (child.acquired.marks / child.acquired.fullMarks) * 100
                                                    : 0
                                                )}%`,
                                              }}
                                            />
                                          </div>
                                        </div>
                                      ) : null}
                                    </div>
                                    {canEdit && child.acquired ? (
                                      <Popover
                                        open={editingAcquiredMarkId === child.acquired.id}
                                        onOpenChange={(open) => {
                                          if (!open) {
                                            setEditingAcquiredMarkId(null);
                                            setMarksDraft('');
                                          }
                                        }}
                                      >
                                        <PopoverTrigger asChild>
                                          <button
                                            type="button"
                                            className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-muted text-muted-foreground hover:text-foreground"
                                            onClick={() => {
                                              setEditingAcquiredMarkId(child.acquired!.id);
                                              setMarksDraft(String(child.acquired!.marks));
                                            }}
                                          >
                                            <Pencil className="h-3.5 w-3.5" />
                                          </button>
                                        </PopoverTrigger>
                                        <PopoverContent className="w-64 space-y-3" align="end">
                                          <p className="text-xs text-muted-foreground">
                                            Full marks: {child.acquired.fullMarks}
                                          </p>
                                          <div className="space-y-1">
                                            <label className="text-xs text-muted-foreground" htmlFor={`marks-${child.id}`}>
                                              Marks
                                            </label>
                                            <input
                                              id={`marks-${child.id}`}
                                              type="number"
                                              className="h-9 w-full rounded-md border bg-background px-3 text-sm"
                                              value={marksDraft}
                                              onChange={(event) => setMarksDraft(event.target.value)}
                                            />
                                          </div>
                                          <Button
                                            className="w-full"
                                            disabled={isPatchingMark}
                                            onClick={() => void handleUpdateMark(child.acquired!.id)}
                                          >
                                            Update
                                          </Button>
                                        </PopoverContent>
                                      </Popover>
                                    ) : null}
                                  </div>
                                </td>
                              </tr>
                            ))
                          : null}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <div className="rounded-xl border bg-card p-4">
                <p className="mb-3 text-sm font-medium text-muted-foreground">Skills evaluated from this course</p>
                {radarData.length ? (
                  <ChartContainer
                    config={{ gpa: { label: 'Overall skills GPA', theme: { light: 'hsl(160 84% 40%)', dark: 'hsl(155 74% 45%)' } } }}
                    className="h-[260px] w-full"
                  >
                    <RadarChart data={radarData}>
                      <PolarGrid />
                      <PolarAngleAxis dataKey="skill" tick={{ fontSize: 11 }} />
                      <Radar dataKey="gpa" stroke="var(--color-gpa)" fill="var(--color-gpa)" fillOpacity={0.35} />
                    </RadarChart>
                  </ChartContainer>
                ) : (
                  <p className="py-12 text-center text-sm text-muted-foreground">No skill evaluation data.</p>
                )}
              </div>

              <div className="rounded-xl border bg-card p-4">
                <p className="mb-3 text-sm font-medium text-muted-foreground">Assignments</p>
                <div className="space-y-3 sm:hidden">
                  {assignmentsBarData.map((item) => {
                    const widthPercent = (item.value / maxAssignmentBarValue) * 100;
                    return (
                      <div key={item.key} className="space-y-1">
                        <div className="flex items-center justify-between gap-2 text-xs">
                          <p className="truncate text-muted-foreground">{item.key}</p>
                          <p className="font-medium">{item.value}</p>
                        </div>
                        <div className="h-2.5 rounded-full bg-muted">
                          <div
                            className="h-2.5 rounded-full bg-primary"
                            style={{ width: `${Math.max(widthPercent, item.value > 0 ? 8 : 0)}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="hidden sm:block">
                  <ChartContainer
                    config={{
                      value: { label: 'Assignments', theme: { light: 'hsl(222 65% 64%)', dark: 'hsl(222 80% 70%)' } },
                    }}
                    className="h-[260px]"
                  >
                    <BarChart data={assignmentsBarData} layout="vertical" margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
                      <CartesianGrid horizontal={false} />
                      <XAxis type="number" allowDecimals={false} />
                      <YAxis type="category" dataKey="key" width={120} />
                      <Bar dataKey="value" fill="var(--color-value)" radius={8} barSize={20} />
                    </BarChart>
                  </ChartContainer>
                </div>
              </div>
            </div>
          )}

          {rightSummaryCard}
        </div>

        <div className="rounded-xl border bg-card p-4">
          <p className="mb-3 text-sm font-medium text-muted-foreground">Attendance</p>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
            <div className="rounded-lg bg-muted p-3 text-center">
              <p className="text-lg font-semibold text-primary">
                {Number(attendanceSummary?.attendance_ratio || 0).toFixed(1)}%
              </p>
              <p className="text-xs text-muted-foreground">Attendance ratio</p>
            </div>
            <div className="rounded-lg bg-muted p-3 text-center">
              <p className="text-lg font-semibold text-emerald-600">{attendanceSummary?.total_present_days || 0}</p>
              <p className="text-xs text-muted-foreground">Present days</p>
            </div>
            <div className="rounded-lg bg-muted p-3 text-center">
              <p className="text-lg font-semibold text-red-600">{attendanceSummary?.total_absent_days || 0}</p>
              <p className="text-xs text-muted-foreground">Absent days</p>
            </div>
            <div className="rounded-lg bg-muted p-3 text-center">
              <p className="text-lg font-semibold">{attendanceSummary?.total_working_days || 0}</p>
              <p className="text-xs text-muted-foreground">Total days</p>
            </div>
            <div className="rounded-lg bg-muted p-3 text-center">
              <p className="text-lg font-semibold">{leaveRequests.length}</p>
              <p className="text-xs text-muted-foreground">Leave requests</p>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setSelectedYear((prev) => prev - 1)}
              className="inline-flex h-8 w-8 items-center justify-center rounded-full border bg-background"
              aria-label="Previous year"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="rounded-full border bg-primary px-4 py-1 text-sm font-medium text-primary-foreground">
              {selectedYear}
            </span>
            <button
              type="button"
              onClick={() => setSelectedYear((prev) => prev + 1)}
              className="inline-flex h-8 w-8 items-center justify-center rounded-full border bg-background"
              aria-label="Next year"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>

          <div className="mt-4 overflow-x-auto">
            <div className="min-w-[980px]">
              <div className="grid grid-cols-[44px_1fr] gap-3">
                <div />
                <div className="relative mb-2 h-4">
                  {attendanceByMonth.weeks.map((_, index) => {
                    const label = attendanceByMonth.monthLabelByWeekIndex.get(index);
                    if (!label) return null;
                    return (
                      <span
                        key={`${label}-${index}`}
                        className="absolute top-0 text-[10px] text-muted-foreground"
                        style={{ left: `${index * 14}px` }}
                      >
                        {label}
                      </span>
                    );
                  })}
                </div>

                <div className="space-y-1">
                  {weekDays.map((day) => (
                    <div key={day} className="h-3 text-[10px] leading-3 text-muted-foreground">
                      {day}
                    </div>
                  ))}
                </div>

                <div className="space-y-1">
                  {weekDays.map((_, dayIndex) => (
                    <div key={dayIndex} className="flex gap-1">
                      {attendanceByMonth.weeks.map((week, weekIndex) => {
                        const cell = week[dayIndex];
                        return (
                          <span
                            key={`${weekIndex}-${dayIndex}`}
                            className={`h-3 w-3 rounded-[2px] ${cell.inYear ? getStatusColorClass(cell.status, cell.leave) : 'bg-transparent'}`}
                            title={`${cell.iso}`}
                          />
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div className="mt-3 flex items-center gap-4 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1"><span className="h-3 w-3 rounded-sm bg-emerald-500 dark:bg-emerald-400" />Present</span>
            <span className="inline-flex items-center gap-1"><span className="h-3 w-3 rounded-sm bg-red-500 dark:bg-red-400" />Absent</span>
            <span className="inline-flex items-center gap-1"><span className="h-3 w-3 rounded-sm bg-amber-300 dark:bg-amber-700" />Leave</span>
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
};

export default CourseStudentAnalyticsPanel;
